import { useEffect, useState } from 'react';

import { looksLikeCode, normaliseCode } from '../../core/authCode';
import { copy } from '../../core/copy';
import { Btn, Win } from '../components';
import { confirmLogin, requestLogin, submitCode, useStore } from '../store';

/** The only screen a person who is not a member ever sees. */
export function Login() {
  const status = useStore((s) => s.loginStatus);
  const codeStatus = useStore((s) => s.codeStatus);
  const devLink = useStore((s) => s.devLink);
  const devCode = useStore((s) => s.devCode);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  // The last complete code this screen sent up, so the six-digit auto-submit fires once per
  // code rather than on every re-render that still has six digits in the box.
  const [submittedCode, setSubmittedCode] = useState('');

  const sending = status === 'sending';
  const sent = status === 'sent';
  const checking = codeStatus === 'working';

  useEffect(() => {
    if (!sent || !looksLikeCode(code) || checking || submittedCode === code) return;
    setSubmittedCode(code);
    void submitCode(email, code);
  }, [sent, code, checking, submittedCode, email]);

  return (
    <div className="centered">
      <img src="/brand/talas-white.svg" alt="Talas" className="logo-mark" />
      <p className="tiny" style={{ color: '#9dc0d1', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
        {copy.brand.tagline}
      </p>

      <Win title={copy.login.title} className="narrow">
        {sent ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (looksLikeCode(code)) void submitCode(email, code);
            }}
          >
            <p style={{ fontSize: 15 }}>{copy.login.sent(email)}</p>
            <p className="muted" style={{ marginTop: 8, marginBottom: 16 }}>
              {copy.login.sentHint}
            </p>

            <label className="lbl" htmlFor="code">
              {copy.login.codeLabel}
            </label>
            <input
              id="code"
              className={`field code-field ${codeStatus === 'invalid' || codeStatus === 'locked' ? 'bad' : ''}`.trim()}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoCorrect="off"
              spellCheck={false}
              placeholder={copy.login.codePlaceholder}
              value={code}
              // Normalised on the way in, so pasting "123 456" out of the mail just works.
              onChange={(event) => setCode(normaliseCode(event.target.value))}
              autoFocus
            />

            {codeStatus === 'invalid' || codeStatus === 'expired' || codeStatus === 'locked' ? (
              <div className="banner bad" style={{ marginTop: 12, marginBottom: 0 }}>
                {codeStatus === 'invalid'
                  ? copy.login.codeBad
                  : codeStatus === 'expired'
                    ? copy.login.codeExpired
                    : copy.login.codeLocked}
              </div>
            ) : null}

            {codeStatus === 'rate-limited' ? (
              <div className="banner" style={{ marginTop: 12, marginBottom: 0 }}>
                {copy.login.tooMany}
              </div>
            ) : null}

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="submit" className="btn primary" disabled={!looksLikeCode(code) || checking}>
                {checking ? copy.login.codeChecking : copy.login.codeSubmit}
              </button>
              <Btn
                variant="ghost"
                size="small"
                onClick={() => {
                  setCode('');
                  setSubmittedCode('');
                  void requestLogin(email);
                }}
              >
                {copy.login.again}
              </Btn>
            </div>

            <p className="tiny muted" style={{ marginTop: 14 }}>
              {copy.login.codeLinkHint}
            </p>

            {devCode ? (
              <p className="tiny" style={{ marginTop: 10 }}>
                <b>{devCode}</b>
              </p>
            ) : null}
            {devLink ? (
              <p className="tiny" style={{ marginTop: 6, wordBreak: 'break-all' }}>
                <a href={devLink}>{devLink}</a>
              </p>
            ) : null}
          </form>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (email.trim()) void requestLogin(email.trim());
            }}
          >
            <p className="muted" style={{ marginBottom: 14 }}>
              {copy.login.lead}
            </p>
            <label className="lbl" htmlFor="email">
              {copy.login.emailLabel}
            </label>
            <input
              id="email"
              className={`field ${status === 'bad-email' || status === 'not-member' ? 'bad' : ''}`.trim()}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              placeholder={copy.login.emailPlaceholder}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />

            {status === 'not-member' ? (
              <div className="banner bad" style={{ marginTop: 12, marginBottom: 0 }}>
                <b>{copy.login.notMember}</b>
                <p className="tiny" style={{ marginTop: 4 }}>
                  {copy.login.notMemberHint}
                </p>
                <p className="tiny" style={{ marginTop: 6 }}>
                  <a href="https://www.skool.com/talasakademija/about" target="_blank" rel="noreferrer noopener">
                    {copy.login.joinLink} ↗
                  </a>
                </p>
              </div>
            ) : null}

            {status === 'rate-limited' ? (
              <div className="banner" style={{ marginTop: 12, marginBottom: 0 }}>
                {copy.login.tooMany}
              </div>
            ) : null}

            {status === 'bad-email' ? (
              <div className="banner bad" style={{ marginTop: 12, marginBottom: 0 }}>
                {copy.login.badEmail}
              </div>
            ) : null}

            {status === 'no-mail' ? (
              <div className="banner" style={{ marginTop: 12, marginBottom: 0 }}>
                Slanje mejlova još nije podešeno. Zamoli Marka da ti pošalje link za ulaz.
                {devLink ? (
                  <p className="tiny" style={{ marginTop: 8, wordBreak: 'break-all' }}>
                    <a href={devLink}>{devLink}</a>
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="submit" className="btn primary" disabled={sending}>
                {sending ? copy.login.sending : copy.login.submit}
              </button>
            </div>

          </form>
        )}
      </Win>
    </div>
  );
}

/**
 * Where the magic link lands.
 *
 * It does not log anyone in on load. Corporate mail scanners follow links in email to check them,
 * and a one-time token that logs in on GET would be burned before the student ever clicks it. So
 * the token sits in the URL fragment — which never reaches the server or a Referer header — and
 * waits for a human to press a button.
 */
export function Confirm() {
  const status = useStore((s) => s.confirmStatus);
  const [token, setToken] = useState('');

  useEffect(() => {
    // Also on hashchange: arriving at /ulaz and *then* getting the fragment is a same-document
    // navigation, so mounting once is not enough to be sure we have seen the token.
    const read = () => {
      const fragment = window.location.hash.replace(/^#/, '');
      if (fragment) setToken(decodeURIComponent(fragment));
    };
    read();
    window.addEventListener('hashchange', read);
    return () => window.removeEventListener('hashchange', read);
  }, []);

  const failed = status === 'expired' || status === 'used' || status === 'invalid';

  return (
    <div className="centered">
      <img src="/brand/talas-white.svg" alt="Talas" className="logo-mark" />
      <Win title={failed ? 'Hm' : copy.login.confirmTitle} className="narrow">
        {failed ? (
          <>
            <p style={{ fontSize: 15 }}>
              {status === 'expired'
                ? copy.login.expired
                : status === 'used'
                  ? copy.login.used
                  : copy.login.invalid}
            </p>
            <p className="muted" style={{ marginTop: 8 }}>
              {copy.login.expiredHint}
            </p>
            <div className="btn-row" style={{ marginTop: 16 }}>
              <Btn
                variant="primary"
                onClick={() => {
                  window.location.hash = '';
                  window.location.href = '/';
                }}
              >
                NAZAD
              </Btn>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 15 }}>{copy.login.confirmLead}</p>
            <div className="btn-row" style={{ marginTop: 16 }}>
              <Btn
                variant="primary"
                disabled={!token || status === 'working'}
                onClick={() => void confirmLogin(token)}
              >
                {status === 'working' ? '…' : copy.login.confirmButton}
              </Btn>
            </div>
          </>
        )}
      </Win>
    </div>
  );
}
